/**
 * Sequence Test Page
 *
 * Test page for the SNES composition engine.
 * Allows loading YAML projects, parsing them, and playing them back
 * using the Transport and SnesSynthInstrument.
 */

import { parseProject, serializeProject, validateProject } from '@/audio/editor/model/YamlCodec.js';
import { pitchToString } from '@/audio/editor/model/NoteParser.js';
import type { Project, Track, Clip, Note } from '@/audio/editor/model/types.js';
import { isSymbolicSample, formatSymbolicRef } from '@/audio/editor/model/types.js';
import { gameSampleLoader } from '@/audio/editor/model/GameSampleLoader.js';
import { Transport, SnesSynthInstrument, getMaxPolyphony } from '@/audio/editor/playback/index.js';
import { decodeBrr } from '@/audio/BrrDecoder.js';
import { SnesSynth } from '@/audio/SnesSynth.js';
import DEMO_CMINOR from '@/audio/editor/examples/demo-song.yaml?raw';
import DEMO_CMAJOR from '@/audio/editor/examples/upbeat-cmajor.yaml?raw';
import DEMO_MANA from '@/audio/editor/examples/mist-of-mana.yaml?raw';

const DEMOS: Record<string, string> = {
  'demo-cminor': DEMO_CMINOR,
  'demo-cmajor': DEMO_CMAJOR,
  'demo-mana': DEMO_MANA,
};

let currentProject: Project | null = null;
let synth: SnesSynth | null = null;
let transport: Transport | null = null;
let instrument: SnesSynthInstrument | null = null;
let isInitialized = false;
let positionUpdateInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Decode base64 BRR to Float32Array PCM samples.
 */
function decodeBrrBase64(base64: string): { samples: Float32Array; loopStart: number } {
  // Decode base64 to Uint8Array
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Decode BRR to Int16 PCM
  const brr = decodeBrr(bytes);

  // Convert Int16 to Float32 (-1 to 1)
  const floatSamples = new Float32Array(brr.samples.length);
  for (let i = 0; i < brr.samples.length; i++) {
    floatSamples[i] = brr.samples[i] / 32768;
  }

  return { samples: floatSamples, loopStart: brr.loopStart };
}

/**
 * Load all samples from a project into the instrument's sample library.
 * This decodes BRR samples and sets up envelope configurations for each instrument.
 */
function loadProjectSamples(project: Project, synthInstrument: SnesSynthInstrument): void {
  // Build a map of sample ID -> InlineSample for quick lookup
  const sampleMap = new Map<
    string,
    { brr: string; loopStart?: number; baseNote: number; sampleRate?: number }
  >();
  for (const sample of project.samples) {
    if ('brr' in sample && sample.brr) {
      sampleMap.set(sample.id, {
        brr: sample.brr,
        loopStart: sample.loopStart,
        baseNote: sample.baseNote,
        sampleRate: sample.sampleRate,
      });
    }
  }

  // Load each instrument
  for (const inst of project.instruments) {
    const sample = sampleMap.get(inst.sampleId);
    if (sample && sample.brr) {
      // Decode BRR to Float32Array PCM
      const decoded = decodeBrrBase64(sample.brr);

      // Use loopStart from sample metadata (game JSON) rather than BRR decoder's loop flag
      // The game metadata loopOffset is more reliable for actual loop points
      const loopStart =
        sample.loopStart ?? (decoded.loopStart >= 0 ? decoded.loopStart : undefined);
      const loopEnabled = loopStart !== undefined && loopStart >= 0;

      // Load sample to instrument's library
      synthInstrument.loadInstrumentSample(inst.id, decoded.samples, {
        baseNote: sample.baseNote,
        sampleRate: sample.sampleRate ?? 32000,
        loopStart,
        loopEnabled,
      });

      // Set envelope from instrument ADSR
      if (inst.type === 'sdsp' && inst.adsr) {
        synthInstrument.setInstrumentEnvelope(inst.id, {
          adsrEnabled: true,
          attack: inst.adsr[0],
          decay: inst.adsr[1],
          sustainLevel: inst.adsr[2],
          sustainDecay: inst.adsr[3],
        });
      }
    } else {
      // No sample data - load a fallback waveform
      // Generate a saw wave as placeholder
      console.warn(`No BRR data for instrument ${inst.id}, using fallback waveform`);
    }
  }
}

export async function renderSequenceTest(appElement: HTMLDivElement): Promise<void> {
  appElement.innerHTML = `
    <div class="sequence-test-container">
      <header class="sequence-test-header">
        <h1>Sequence Test</h1>
        <p>SNES Composition Engine - Playback Test</p>
      </header>

      <div class="playback-section">
        <div class="demo-selector">
          <label for="demo-select">Demo:</label>
          <select id="demo-select">
            <option value="demo-cminor">C Minor (Moody)</option>
            <option value="demo-cmajor">C Major (Upbeat)</option>
            <option value="demo-mana">Mist of the Mana Tree</option>
          </select>
        </div>
        <div class="transport-controls">
          <button id="play-btn" class="transport-btn play-btn">Play</button>
          <button id="pause-btn" class="transport-btn" disabled>Pause</button>
          <button id="stop-btn" class="transport-btn" disabled>Stop</button>
          <button id="loop-btn" class="transport-btn loop-btn">Loop</button>
        </div>
        <div class="transport-status">
          <span id="transport-state">Ready</span>
          <span id="transport-position">0.00 beats</span>
        </div>
        <div class="position-slider-container">
          <label for="position-slider">Seek:</label>
          <input type="range" id="position-slider" min="0" max="128" step="0.25" value="0">
          <span id="position-display">0</span>
        </div>
      </div>

      <main class="sequence-test-main">
        <div class="sequence-test-editor">
          <h2>YAML Input</h2>
          <textarea id="yaml-input" spellcheck="false"></textarea>
          <div class="sequence-test-buttons">
            <button id="parse-btn">Parse YAML</button>
            <button id="serialize-btn">Serialize Project</button>
          </div>
        </div>

        <div class="sequence-test-output">
          <h2>Parsed Project</h2>
          <div id="project-display" class="project-display">
            <p class="placeholder">Load or parse a project to see its structure</p>
          </div>
        </div>
      </main>

      <style>
        .sequence-test-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px;
          font-family: 'Courier New', monospace;
          color: #e0e0e0;
        }

        .sequence-test-header {
          text-align: center;
          margin-bottom: 20px;
        }

        .sequence-test-header h1 {
          margin: 0;
          color: #4fc3f7;
        }

        .sequence-test-header p {
          margin: 5px 0 0;
          color: #888;
        }

        .sequence-test-main {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .demo-selector {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 15px;
        }

        .demo-selector label {
          color: #888;
          font-size: 14px;
        }

        .demo-selector select {
          padding: 6px 12px;
          background: #333;
          border: 1px solid #555;
          color: #e0e0e0;
          font-family: inherit;
          font-size: 14px;
          cursor: pointer;
        }

        .demo-selector select:hover {
          background: #444;
        }

        .sequence-test-editor h2,
        .sequence-test-output h2 {
          margin: 0 0 10px;
          font-size: 16px;
          color: #888;
        }

        #yaml-input {
          width: 100%;
          height: 400px;
          background: #1a1a2e;
          border: 1px solid #333;
          color: #e0e0e0;
          font-family: 'Courier New', monospace;
          font-size: 12px;
          padding: 10px;
          resize: vertical;
          box-sizing: border-box;
        }

        .sequence-test-buttons {
          margin-top: 10px;
          display: flex;
          gap: 10px;
        }

        .sequence-test-buttons button {
          padding: 8px 16px;
          background: #333;
          border: 1px solid #555;
          color: #e0e0e0;
          cursor: pointer;
          font-family: inherit;
        }

        .sequence-test-buttons button:hover {
          background: #444;
        }

        .project-display {
          background: #1a1a2e;
          border: 1px solid #333;
          padding: 15px;
          height: 400px;
          overflow-y: auto;
          font-size: 12px;
        }

        .project-display .placeholder {
          color: #666;
          font-style: italic;
        }

        .playback-section {
          background: #1a1a2e;
          border: 1px solid #333;
          padding: 15px 20px;
          margin-bottom: 20px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 15px;
        }

        .playback-section .demo-selector {
          margin-bottom: 0;
        }

        .transport-controls {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .transport-btn {
          padding: 10px 20px;
          background: #333;
          border: 1px solid #555;
          color: #e0e0e0;
          cursor: pointer;
          font-family: inherit;
          font-size: 14px;
        }

        .transport-btn:hover:not(:disabled) {
          background: #444;
        }

        .transport-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .transport-btn.play-btn {
          background: #2e7d32;
          border-color: #4caf50;
        }

        .transport-btn.play-btn:hover:not(:disabled) {
          background: #388e3c;
        }

        #stop-btn {
          background: #c62828;
          border-color: #f44336;
        }

        #stop-btn:hover:not(:disabled) {
          background: #d32f2f;
        }

        .loop-btn {
          background: #333;
          border-color: #555;
        }

        .loop-btn.active {
          background: #1565c0;
          border-color: #2196f3;
          color: #fff;
        }

        .loop-btn:hover:not(:disabled) {
          background: #444;
        }

        .loop-btn.active:hover:not(:disabled) {
          background: #1976d2;
        }

        .transport-status {
          display: flex;
          gap: 15px;
          font-size: 13px;
          color: #aaa;
        }

        .position-slider-container {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
        }

        .position-slider-container label {
          color: #888;
        }

        #position-slider {
          width: 120px;
        }

        #position-display {
          color: #aaa;
          min-width: 30px;
        }

        .project-section {
          margin-bottom: 20px;
        }

        .project-section h3 {
          margin: 0 0 10px;
          color: #4fc3f7;
          font-size: 14px;
          border-bottom: 1px solid #333;
          padding-bottom: 5px;
        }

        .project-info {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 5px 15px;
        }

        .project-info .label {
          color: #888;
        }

        .project-info .value {
          color: #e0e0e0;
        }

        .track-card {
          background: #252540;
          border: 1px solid #333;
          padding: 10px;
          margin-bottom: 10px;
        }

        .track-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .track-header h4 {
          margin: 0;
          color: #81c784;
        }

        .track-controls {
          display: flex;
          gap: 5px;
        }

        .track-mute-btn,
        .track-solo-btn {
          width: 24px;
          height: 24px;
          padding: 0;
          border: 1px solid #555;
          background: #333;
          color: #888;
          font-size: 11px;
          font-weight: bold;
          cursor: pointer;
          border-radius: 3px;
        }

        .track-mute-btn:hover,
        .track-solo-btn:hover {
          background: #444;
        }

        .track-mute-btn.active {
          background: #c62828;
          border-color: #f44336;
          color: #fff;
        }

        .track-solo-btn.active {
          background: #f9a825;
          border-color: #ffc107;
          color: #000;
        }

        .clip-card {
          background: #1e1e30;
          padding: 8px;
          margin: 5px 0;
          border-left: 3px solid #4fc3f7;
        }

        .clip-card .clip-header {
          color: #aaa;
          margin-bottom: 5px;
        }

        .note-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
        }

        .note-chip {
          background: #333;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 11px;
        }

        .note-chip .pitch {
          color: #ffb74d;
        }

        .note-chip .time {
          color: #888;
        }

        .validation-errors {
          background: #4a1a1a;
          border: 1px solid #ff5252;
          padding: 10px;
          margin-bottom: 15px;
        }

        .validation-errors h4 {
          margin: 0 0 5px;
          color: #ff5252;
        }

        .validation-errors ul {
          margin: 0;
          padding-left: 20px;
        }

        .parse-error {
          background: #4a1a1a;
          border: 1px solid #ff5252;
          padding: 15px;
          color: #ff8a80;
        }

        @media (max-width: 900px) {
          .sequence-test-main {
            grid-template-columns: 1fr;
          }

          #yaml-input,
          .project-display {
            height: 300px;
          }
        }
      </style>
    </div>
  `;

  const yamlInput = document.getElementById('yaml-input') as HTMLTextAreaElement;
  const parseBtn = document.getElementById('parse-btn') as HTMLButtonElement;
  const demoSelect = document.getElementById('demo-select') as HTMLSelectElement;
  const serializeBtn = document.getElementById('serialize-btn') as HTMLButtonElement;
  const projectDisplay = document.getElementById('project-display') as HTMLDivElement;

  const playBtn = document.getElementById('play-btn') as HTMLButtonElement;
  const pauseBtn = document.getElementById('pause-btn') as HTMLButtonElement;
  const stopBtn = document.getElementById('stop-btn') as HTMLButtonElement;
  const loopBtn = document.getElementById('loop-btn') as HTMLButtonElement;
  const transportState = document.getElementById('transport-state') as HTMLSpanElement;
  const transportPosition = document.getElementById('transport-position') as HTMLSpanElement;
  const positionSlider = document.getElementById('position-slider') as HTMLInputElement;
  const positionDisplay = document.getElementById('position-display') as HTMLSpanElement;

  // Check URL for demo parameter
  const urlParams = new URLSearchParams(window.location.search);
  const initialDemo = urlParams.get('demo');
  if (initialDemo && DEMOS[initialDemo]) {
    demoSelect.value = initialDemo;
  }

  // Load demo on start
  yamlInput.value = DEMOS[demoSelect.value];

  parseBtn.addEventListener('click', async () => {
    try {
      parseBtn.textContent = 'Parsing...';
      parseBtn.disabled = true;

      // Parse YAML to project
      let project = parseProject(yamlInput.value);

      // Check if there are any symbolic samples that need resolution
      const hasSymbolicSamples = project.samples.some(isSymbolicSample);
      if (hasSymbolicSamples) {
        parseBtn.textContent = 'Loading samples...';
        project = await gameSampleLoader.resolveProject(project);
      }

      currentProject = project;
      renderProjectDisplay(projectDisplay, currentProject);
      setupTrackControls(projectDisplay);

      // Load samples and update transport if initialized
      if (instrument && currentProject) {
        loadProjectSamples(currentProject, instrument);
      }
      if (transport && currentProject) {
        transport.setProject(currentProject);
      }

      parseBtn.textContent = 'Parse YAML';
      parseBtn.disabled = false;
    } catch (error) {
      renderError(projectDisplay, error as Error);
      parseBtn.textContent = 'Parse YAML';
      parseBtn.disabled = false;
    }
  });

  demoSelect.addEventListener('change', () => {
    yamlInput.value = DEMOS[demoSelect.value];
    // Update URL with selected demo
    const url = new URL(window.location.href);
    url.searchParams.set('demo', demoSelect.value);
    history.replaceState(null, '', url);
    parseBtn.click();
  });

  serializeBtn.addEventListener('click', () => {
    if (!currentProject) {
      alert('No project loaded. Parse a YAML first.');
      return;
    }
    const yaml = serializeProject(currentProject);
    yamlInput.value = yaml;
  });

  // Initialize audio (called automatically on first play)
  async function initializeAudio() {
    // Create synth and initialize
    synth = new SnesSynth();
    await synth.init();
    await synth.resume();

    // Create instrument adapter
    instrument = new SnesSynthInstrument(synth);

    // Set default volume for all voices
    for (let i = 0; i < 8; i++) {
      synth.setVolume(i, 0.3, 0.3);
    }

    // Load project samples if project is already parsed
    if (currentProject) {
      loadProjectSamples(currentProject, instrument);
    }

    // Create transport
    transport = new Transport();
    transport.setInstrument(instrument);

    // Sync loop state from UI (in case user toggled before init)
    const loopEnabled = loopBtn.classList.contains('active');
    transport.setLoop(loopEnabled);

    // Load project if already parsed
    if (currentProject) {
      transport.setProject(currentProject);
    }

    // Set up callbacks
    transport.setOnStateChange((state) => {
      transportState.textContent =
        state === 'playing' ? 'Playing' : state === 'paused' ? 'Paused' : 'Stopped';
      updateButtonStates();
    });

    transport.setOnPositionChange((beat) => {
      transportPosition.textContent = `${beat.toFixed(2)} beats`;
      if (!positionSlider.matches(':active')) {
        positionSlider.value = String(beat);
        positionDisplay.textContent = String(beat.toFixed(1));
      }
    });

    isInitialized = true;

    // Start position update interval for smoother updates
    positionUpdateInterval = setInterval(() => {
      if (transport && transport.getState() === 'playing') {
        const beat = transport.getPositionBeat();
        transportPosition.textContent = `${beat.toFixed(2)} beats`;
        if (!positionSlider.matches(':active')) {
          positionSlider.value = String(beat);
          positionDisplay.textContent = String(beat.toFixed(1));
        }
      }
    }, 50);
  }

  playBtn.addEventListener('click', async () => {
    if (!currentProject) {
      alert('Please parse a project first');
      return;
    }

    // Auto-initialize on first play
    if (!isInitialized) {
      playBtn.textContent = 'Loading...';
      playBtn.disabled = true;
      try {
        await initializeAudio();
      } catch (error) {
        console.error('Failed to initialize audio:', error);
        playBtn.textContent = 'Play';
        playBtn.disabled = false;
        alert('Failed to initialize audio. Please try again.');
        return;
      }
      playBtn.textContent = 'Play';
    }

    transport?.play();
    updateButtonStates();
  });

  pauseBtn.addEventListener('click', () => {
    transport?.pause();
    updateButtonStates();
  });

  stopBtn.addEventListener('click', () => {
    transport?.stop();
    updateButtonStates();
  });

  loopBtn.addEventListener('click', () => {
    if (transport) {
      const newLoopState = !transport.isLooping();
      transport.setLoop(newLoopState);
      loopBtn.classList.toggle('active', newLoopState);
    } else {
      // Toggle visual state even before transport is initialized
      loopBtn.classList.toggle('active');
    }
  });

  // Position slider
  positionSlider.addEventListener('input', () => {
    const beat = parseFloat(positionSlider.value);
    positionDisplay.textContent = String(beat.toFixed(1));
  });

  positionSlider.addEventListener('change', () => {
    const beat = parseFloat(positionSlider.value);
    transport?.seekToBeat(beat);
  });

  function updateButtonStates() {
    const state = transport?.getState() ?? 'stopped';

    playBtn.disabled = !currentProject || state === 'playing';
    pauseBtn.disabled = state !== 'playing';
    stopBtn.disabled = state === 'stopped';
  }

  // Auto-parse on load
  parseBtn.click();

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    if (positionUpdateInterval) {
      clearInterval(positionUpdateInterval);
    }
  });
}

function setupTrackControls(container: HTMLDivElement): void {
  // Mute buttons
  container.querySelectorAll('.track-mute-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const trackId = (e.target as HTMLButtonElement).dataset.trackId;
      if (!currentProject || !trackId) return;

      const track = currentProject.tracks.find((t) => t.id === trackId);
      if (track) {
        (track as { mute: boolean }).mute = !track.mute;
        btn.classList.toggle('active', track.mute);

        // Update transport with modified project
        if (transport) {
          transport.setProject(currentProject);
        }
      }
    });
  });

  // Solo buttons
  container.querySelectorAll('.track-solo-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const trackId = (e.target as HTMLButtonElement).dataset.trackId;
      if (!currentProject || !trackId) return;

      const track = currentProject.tracks.find((t) => t.id === trackId);
      if (track) {
        (track as { solo: boolean }).solo = !track.solo;
        btn.classList.toggle('active', track.solo);

        // If any track is solo'd, mute all non-solo'd tracks
        const anySolo = currentProject.tracks.some((t) => t.solo);
        if (anySolo) {
          currentProject.tracks.forEach((t) => {
            (t as { mute: boolean }).mute = !t.solo;
          });
          // Update all mute button states
          container.querySelectorAll('.track-mute-btn').forEach((muteBtn) => {
            const muteTrackId = (muteBtn as HTMLButtonElement).dataset.trackId;
            const muteTrack = currentProject!.tracks.find((t) => t.id === muteTrackId);
            if (muteTrack) {
              muteBtn.classList.toggle('active', muteTrack.mute);
            }
          });
        } else {
          // No solo, unmute all
          currentProject.tracks.forEach((t) => {
            (t as { mute: boolean }).mute = false;
          });
          container.querySelectorAll('.track-mute-btn').forEach((muteBtn) => {
            muteBtn.classList.remove('active');
          });
        }

        // Update transport with modified project
        if (transport) {
          transport.setProject(currentProject);
        }
      }
    });
  });
}

function renderProjectDisplay(container: HTMLDivElement, project: Project): void {
  const errors = validateProject(project);
  const maxPolyphony = getMaxPolyphony(project);

  let html = '';

  // Validation errors
  if (errors.length > 0) {
    html += `
      <div class="validation-errors">
        <h4>Validation Warnings</h4>
        <ul>
          ${errors.map((e) => `<li>${escapeHtml(e)}</li>`).join('')}
        </ul>
      </div>
    `;
  }

  // Project info
  html += `
    <div class="project-section">
      <h3>Project Info</h3>
      <div class="project-info">
        <span class="label">Name:</span>
        <span class="value">${escapeHtml(project.name)}</span>
        <span class="label">Tempo:</span>
        <span class="value">${project.tempo} BPM</span>
        <span class="label">Time Signature:</span>
        <span class="value">${project.timeSignature[0]}/${project.timeSignature[1]}</span>
        <span class="label">Samples:</span>
        <span class="value">${project.samples.length}</span>
        <span class="label">Instruments:</span>
        <span class="value">${project.instruments.length}</span>
        <span class="label">Tracks:</span>
        <span class="value">${project.tracks.length}</span>
        <span class="label">Max Polyphony:</span>
        <span class="value">${maxPolyphony} ${maxPolyphony > 8 ? '(exceeds S-DSP limit!)' : ''}</span>
      </div>
    </div>
  `;

  // Samples
  if (project.samples.length > 0) {
    html += `
      <div class="project-section">
        <h3>Samples</h3>
        ${project.samples
          .map(
            (s) => `
          <div class="project-info" style="margin-bottom: 10px;">
            <span class="label">ID:</span>
            <span class="value">${escapeHtml(s.id)}</span>
            <span class="label">Base Note:</span>
            <span class="value">${s.baseNote !== undefined ? pitchToString(s.baseNote) : 'Auto'}</span>
            ${
              isSymbolicSample(s)
                ? `
            <span class="label">Ref:</span>
            <span class="value">${escapeHtml(formatSymbolicRef(s.ref))}</span>
            `
                : `
            <span class="label">Loop Start:</span>
            <span class="value">${s.loopStart ?? 'None'}</span>
            `
            }
          </div>
        `
          )
          .join('')}
      </div>
    `;
  }

  // Instruments
  if (project.instruments.length > 0) {
    html += `
      <div class="project-section">
        <h3>Instruments</h3>
        ${project.instruments
          .map(
            (i) => `
          <div class="project-info" style="margin-bottom: 10px;">
            <span class="label">ID:</span>
            <span class="value">${escapeHtml(i.id)}</span>
            <span class="label">Type:</span>
            <span class="value">${i.type}</span>
            <span class="label">Sample:</span>
            <span class="value">${escapeHtml(i.sampleId)}</span>
            <span class="label">ADSR:</span>
            <span class="value">[${i.adsr.join(', ')}]</span>
          </div>
        `
          )
          .join('')}
      </div>
    `;
  }

  // Tracks
  if (project.tracks.length > 0) {
    html += `
      <div class="project-section">
        <h3>Tracks</h3>
        ${project.tracks.map((track) => renderTrack(track)).join('')}
      </div>
    `;
  }

  // Global Automation
  if (project.globalAutomation.echo || project.globalAutomation.masterVolume) {
    html += `
      <div class="project-section">
        <h3>Global Automation</h3>
        <div class="project-info">
    `;
    if (project.globalAutomation.echo && project.globalAutomation.echo.length > 0) {
      const echo = project.globalAutomation.echo[0];
      html += `
          <span class="label">Echo Delay:</span>
          <span class="value">${echo.delay ?? '-'}ms</span>
          <span class="label">Echo Feedback:</span>
          <span class="value">${echo.feedback ?? '-'}</span>
      `;
    }
    html += `
        </div>
      </div>
    `;
  }

  container.innerHTML = html;
}

function renderTrack(track: Track): string {
  const clipCount = track.clips.length;
  const noteCount = track.clips.reduce((sum, c) => sum + c.notes.length, 0);

  return `
    <div class="track-card" data-track-id="${escapeHtml(track.id)}">
      <div class="track-header">
        <h4>${escapeHtml(track.name)}</h4>
        <div class="track-controls">
          <button class="track-mute-btn ${track.mute ? 'active' : ''}" data-track-id="${escapeHtml(track.id)}">M</button>
          <button class="track-solo-btn ${track.solo ? 'active' : ''}" data-track-id="${escapeHtml(track.id)}">S</button>
        </div>
      </div>
      <div class="project-info">
        <span class="label">ID:</span>
        <span class="value">${escapeHtml(track.id)}</span>
        <span class="label">Instrument:</span>
        <span class="value">${escapeHtml(track.instrumentId)}</span>
        <span class="label">Clips:</span>
        <span class="value">${clipCount}</span>
        <span class="label">Total Notes:</span>
        <span class="value">${noteCount}</span>
      </div>
      ${track.clips.map((clip) => renderClip(clip)).join('')}
    </div>
  `;
}

function renderClip(clip: Clip): string {
  return `
    <div class="clip-card">
      <div class="clip-header">
        <strong>${escapeHtml(clip.id)}</strong> |
        Start: Beat ${clip.start} |
        Duration: ${clip.duration} beats |
        ${clip.notes.length} notes
      </div>
      <div class="note-grid">
        ${clip.notes.map((note) => renderNote(note)).join('')}
      </div>
    </div>
  `;
}

function renderNote(note: Note): string {
  const pitch = pitchToString(note.pitch);
  const velocity = note.velocity !== 100 ? ` ${note.velocity}%` : '';
  return `
    <span class="note-chip">
      <span class="time">${note.time}:</span><span class="pitch">${pitch}</span>/${note.duration}${velocity}
    </span>
  `;
}

function renderError(container: HTMLDivElement, error: Error): void {
  container.innerHTML = `
    <div class="parse-error">
      <strong>Parse Error:</strong><br>
      ${escapeHtml(error.message)}
    </div>
  `;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
